import React, { forwardRef, useImperativeHandle } from "react";
import "./PassportSizeControls.css";
import PresetSelectDropdown from "../../../../../components/ui/PresetSelectDropdown";
import { getCategoryPresetPickerCopy } from "../../../../../config/dashboard/dashboardCategoryModalConfig";
import {
  PASSPORT_CATEGORY_ID,
  resolvePassportSpec,
  clampRatio,
} from "../../../../../utils/passportPresetUtils";

/**
 * AI Photo Studio shared controls — country / size.
 * Background, head framing, and DPI stay on the request. The user does not edit them.
 */
const PassportSizeControls = forwardRef(function PassportSizeControls(
  {
    selectedPreset = null,
    countryOptions = [],
    onCountryChange,
    onSuggestCrop,
    onGenerate,
    busy = false,
    presetsLoading = false,
    suggestedCrop = null,
    beforeActions = null,
    generateLabel = "Generate",
    generateDisabled = false,
    hideActions = false,
  },
  ref
) {
  const pickerCopy = React.useMemo(
    () => getCategoryPresetPickerCopy({ category_id: PASSPORT_CATEGORY_ID }),
    []
  );
  const spec = React.useMemo(
    () => resolvePassportSpec(selectedPreset),
    [selectedPreset]
  );
  const helperText = selectedPreset?.helper_text || {};

  const [crop, setCrop] = React.useState(null);

  React.useEffect(() => {
    setCrop(null);
  }, [selectedPreset?.id]);

  React.useEffect(() => {
    if (suggestedCrop && typeof suggestedCrop === "object") {
      const { crop_left, crop_top, crop_w, crop_h } = suggestedCrop;
      if (
        crop_left != null &&
        crop_top != null &&
        crop_w != null &&
        crop_h != null
      ) {
        setCrop({ crop_left, crop_top, crop_w, crop_h });
      }
    }
  }, [suggestedCrop]);

  const handleCountrySelect = (preset) => {
    if (preset && typeof onCountryChange === "function") {
      onCountryChange(preset);
    }
  };

  const buildBaseOpts = React.useCallback(
    () => ({
      w_mm: Number(spec.w_mm),
      h_mm: Number(spec.h_mm),
      head_h_ratio_min: clampRatio(spec.head_h_ratio_min),
      head_h_ratio_max: clampRatio(spec.head_h_ratio_max),
      eye_line_from_top: clampRatio(spec.eye_line_from_top),
    }),
    [spec]
  );

  const handleSuggestCrop = React.useCallback(() => {
    if (typeof onSuggestCrop !== "function") return;
    onSuggestCrop(buildBaseOpts());
  }, [buildBaseOpts, onSuggestCrop]);

  const handleGenerate = React.useCallback(() => {
    if (typeof onGenerate !== "function") return;
    const opts = {
      ...buildBaseOpts(),
      bg_hex: spec.bg_hex || "#FFFFFF",
      white_background: spec.white_background !== false,
      print_sheet: false,
      output_format: "png",
    };
    if (crop) {
      opts.crop_left = crop.crop_left;
      opts.crop_top = crop.crop_top;
      opts.crop_w = crop.crop_w;
      opts.crop_h = crop.crop_h;
    }
    onGenerate(opts);
  }, [buildBaseOpts, crop, onGenerate, spec.bg_hex, spec.white_background]);

  useImperativeHandle(
    ref,
    () => ({
      suggestCrop: handleSuggestCrop,
      generate: handleGenerate,
    }),
    [handleGenerate, handleSuggestCrop]
  );

  const formatMm = (value) => {
    const n = Number(value);
    if (Number.isNaN(n)) return "";
    return Number.isInteger(n) ? String(n) : String(n);
  };

  return (
    <div className="passport-control-menu-container">
      <div className="passport-field">
        <PresetSelectDropdown
          id="appm-passport-size-select"
          label="Size"
          presets={countryOptions}
          selectedPreset={selectedPreset}
          onSelect={handleCountrySelect}
          loading={presetsLoading}
          disabled={busy}
          pickerCopy={pickerCopy}
          categoryId={PASSPORT_CATEGORY_ID}
          className="passport-preset-select"
        />
        <p className="passport-size-dimensions">
          <span>Width (mm): {formatMm(spec.w_mm)}</span>
          <span>Height (mm): {formatMm(spec.h_mm)}</span>
        </p>
        {helperText.size ? (
          <p className="passport-helper-text">{helperText.size}</p>
        ) : null}
      </div>

      {crop ? (
        <div className="passport-crop-summary">
          <p className="passport-section-label">Suggested crop</p>
          <p className="passport-crop-text">
            x:{crop.crop_left} y:{crop.crop_top} · {crop.crop_w}×{crop.crop_h}
          </p>
          <button
            type="button"
            className="passport-link-btn"
            onClick={() => setCrop(null)}
          >
            Clear crop
          </button>
        </div>
      ) : null}

      {beforeActions}

      {!hideActions ? (
        <div className="passport-actions">
          <button
            type="button"
            className="passport-secondary-btn"
            onClick={handleSuggestCrop}
            disabled={busy}
          >
            {busy ? "Working…" : "Suggest crop"}
          </button>
          <button
            type="button"
            className="passport-primary-btn"
            onClick={handleGenerate}
            disabled={busy || generateDisabled}
          >
            {busy ? "Generating…" : generateLabel}
          </button>
        </div>
      ) : null}
    </div>
  );
});

export default PassportSizeControls;
